import { filter, find, length, propEq, propOr } from 'ramda';
import { isNotNilOrEmpty } from '@@serverHelpers/index.mjs';

/**
 * Retourne true si le niveau (pricing) possède une entrée disponible pour la structure tarifaire donnée.
 *
 * Règles :
 * - Entrée absente dans totalPrices → indisponible (pas de tarif pour cette structure).
 * - Entrée présente sans champ isAvailable (ancien format / mono-zone) → disponible.
 * - Entrée présente avec isAvailable === false → indisponible.
 * - Toute autre valeur de isAvailable (true, undefined, null…) → disponible.
 */
const isLevelAvailableFor = (tariffStructureCode) => (pricing) => {
  const entry = find(
    propEq('tariffStructureCode', tariffStructureCode),
    pricing.totalPrices || []
  );
  if (!entry) return false;
  return entry.isAvailable !== false;
};

/**
 * Filtre les niveaux (pricings) en ne gardant que ceux disponibles pour la structure tarifaire donnée.
 */
const filterAvailablePricings = (tariffStructureCode, pricings) =>
  filter(isLevelAvailableFor(tariffStructureCode), pricings);

/**
 * Filtre les structures tarifaires sélectionnables : ne garde que celles pour lesquelles
 * au moins un niveau est disponible. Évite de proposer une structure qui afficherait une grille vide.
 */
const filterSelectableTariffStructures = (tariffStructures, pricings) =>
  filter(
    (ts) => isNotNilOrEmpty(filterAvailablePricings(ts.value, pricings)),
    tariffStructures
  );

/**
 * Zones de couverture où le niveau est éligible pour la structure tarifaire
 */
const getEligibleZones = (tariffStructureCode) => (pricing) => {
  const entry = find(
    propEq('tariffStructureCode', tariffStructureCode),
    pricing.totalPrices || []
  );
  const eligibleZones = propOr([], 'eligibleZones', entry);
  const zoneCount =
    length(eligibleZones) + length(propOr([], 'unavailableZones', entry));

  return zoneCount > 1 ? eligibleZones : [];
};

export {
  isLevelAvailableFor,
  filterAvailablePricings,
  filterSelectableTariffStructures,
  getEligibleZones
};
